import React from "react";
import QuickView from "./QuickView/QuickView";
import DeviceContext, { DeviceContextI } from "src/contexts/DeviceContext";
import { getThemeName, Theme } from "src/utils/themeUtils";
import { getCurrentLocale } from "src/utils/localeUtils";
import { getBDSDeviceType, getBDSLocale, getBDSMarketplace, getBDSOperatingSystem, getDeviceDetails, NativeAPI } from "src/utils/deviceUtils";
import Translations from "./Translations/Translations";
import debug from "src/utils/debugUtils";
import { disableMetrics, setQvSessionId } from "src/utils/metricsUtils";
import { setOverrideUserAgent, setRequestDomain, setUseRequestDomainOnRequests } from "src/utils/ajaxUtils";
import { getSurfaceType } from "src/utils/deviceUtils"
import { uuidv4 } from "src/utils/uuid";
import weblabUtils from "src/utils/weblabUtils";
import "@amzn/books-design-system-web/build/dist/src/components/contextProviders/RenderContextProvider";
import HeartCxManager from "./HeartCx/HeartCxManager";

type PropTypes = object;

let localeChangeCallback: (locale: string) => void | undefined;
const onLocaleChange = (callback: (locale: string) => void) => {
    localeChangeCallback = callback;
};

window.addEventListener("languagechange", () => {
    if (typeof localeChangeCallback === "function") {
        localeChangeCallback(navigator.language);
    }
});

let themeChangeCallback: (theme: Theme) => void | undefined;
const onThemeChange = (callback: (theme: Theme) => void) => {
    themeChangeCallback = callback;
};

window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (typeof themeChangeCallback === "function") {
        const theme = window.matchMedia("(prefers-color-scheme: dark)").matches ? Theme.DARK : Theme.LIGHT;
        themeChangeCallback(theme);
    }
});

let visibilityChangedCallback: (focused: boolean) => void | undefined;
const onVisibilityChange = (callback: (isVisible: boolean) => void) => {
    visibilityChangedCallback = callback;
};

window.addEventListener("visibilitychange", () => {
    if (typeof visibilityChangedCallback === "function") {
        visibilityChangedCallback(document.visibilityState === 'visible');
    }
});

const getDomain = () => {
    const domain = window.quickViewData?.domain || window.location.host.replace(/.*amazon/i, "amazon");
    if (domain.startsWith("amazon.") && !domain.endsWith("dev")) {
        return domain.toLowerCase();
    } else {
        return "amazon.com";
    }
};

const getIOSAssocHandle = (deviceDomain: string): string => {
    const iosAssocHandleMap: { [key: string]: string } = {
        'amazon.com': 'usflex',
        //other assocHandles can be added as we progress to enable SSO WW.
    };
    return iosAssocHandleMap[deviceDomain.toLowerCase()] || 'usflex';
};

const domainToCountryCodeMap: { [key: string]: string } = {
    "amazon.com": "USA",
};
const domainMatchesAppleCountryCode = (domain: string, appleCountryCode: string) => {
    return domainToCountryCodeMap[domain] === appleCountryCode || appleCountryCode === "UNKNOWN";
};

let reKindle2Eligibility = false;
const checkRekindle2Eligibility = async () => {
    await NativeAPI.checkExternalLinkAccountEligibility()
        ?.then(result => {
            reKindle2Eligibility = result === true;
        });
};

let reKindleWeblabsEnabled = false;
let reKindle2WeblabsEnabled = false;
export class App extends React.Component<PropTypes, DeviceContextI> {
    constructor(props: PropTypes) {
        super(props);

        const fullUrlsSupported = window.quickViewData?.experimentalFeatureMap?.aapiAjaxUrlsEnabled === true
                               || window.quickViewData?.experimentalFeatures?.aapiAjaxUrlsEnabled === true;
        setUseRequestDomainOnRequests(fullUrlsSupported);

        if (debug.get("disableMetrics")) {
            disableMetrics();
        }

        const domain = getDomain();
        setRequestDomain(domain);
        setQvSessionId(`${getSurfaceType()}.${debug.versionInfo.buildDate}.${debug.versionInfo.mainJs}.${domain}.${uuidv4()}`);

        const iOSMESTreatment = window.quickViewData?.experimentalFeatures?.treatmentMap?.["KINDLE_QV_GENERIC_IOS_959939"] ?? "C";
        const iOSAFRTreatment = window.quickViewData?.experimentalFeatures?.treatmentMap?.["KINDLE_QV_GENERIC_IOS_959936"] ?? "C";
        const iOSBypassAskConsentTreatment = window.quickViewData?.experimentalFeatures?.treatmentMap?.["KINDLE_QV_GENERIC_IOS_1255353"] ?? "C";

        const iOSReKindleTreatment = window.quickViewData?.experimentalFeatures?.treatmentMap?.["BOOKS_REKINDLE_LAUNCH_1190038"] ?? "C";
        const iOSReKindleForceOffTreatment = window.quickViewData?.experimentalFeatures?.treatmentMap?.["BOOKS_REKINDLE_QUICKVIEW_FORCEOFF_1193837"] ?? "C";
        reKindleWeblabsEnabled = iOSReKindleForceOffTreatment !== "T1" && iOSReKindleTreatment === "T1";
        reKindle2WeblabsEnabled = iOSReKindleForceOffTreatment !== "T1" && iOSReKindleTreatment === "T2";
        const iOSReKindleSSoTreatment =
            window.quickViewData?.experimentalFeatures?.treatmentMap?.["BOOKS_REKINDLE_INTERGRATION_FOR_SSO_1300185"] ?? "C";
        const ssoEnabled = iOSReKindleSSoTreatment === "T1" || debug.get("enableReKindleSSO");
        const appleCountryCode = window.quickViewData?.appleCor;
        const reKindleCountryEnabled = appleCountryCode ? domainMatchesAppleCountryCode(domain, appleCountryCode) : true;
        const isTeenAccount = window.quickViewData?.isTeenAccount === true;
        const shouldDisableSeeAllDetails = (reKindleWeblabsEnabled && appleCountryCode && !reKindleCountryEnabled) || isTeenAccount || debug.get("forceTeenAccount");

        const deviceDetails = getDeviceDetails();
        const isBeta = window.quickViewData?.experimentalFeatures?.isBeta === true;
        const enableMes = (isBeta || iOSMESTreatment === "T1" || debug.get("enableMoreLikeThis"));

        // Weblab logic for enabling the KU Upsell feature would go here.
        const enableKuUpsell = debug.get("enableKuUpsell");

        const heartCxTreatment = window.quickViewData?.experimentalFeatures?.treatmentMap?.["KINDLE_QV_GENERIC_IOS_1255348"] ?? "C";
        const kuRebrandTreatment = window.quickViewData?.experimentalFeatures?.treatmentMap?.["BOOKS_KU_REBRAND_PRIMARY_LAUNCH_1236037"] ?? "C";
        // Weblab for Return and Borrow with Save feature
        const returnBorrowSaveTreatment = window.quickViewData?.experimentalFeatures?.treatmentMap?.["BOOKS_KFI_YOUR_LISTS_CX_MIGRATION_1277667"] ?? "C";

        // Weblab to gate the CTA string logic for Add To Library For Free purchase option
        const enableAapiCtaTreatment = window.quickViewData?.experimentalFeatures?.treatmentMap?.["KINDLE_QV_GENERIC_IOS_1255350"] ?? "C";
        const enableAapiCta = (isBeta && enableAapiCtaTreatment === "T2") || enableAapiCtaTreatment === "T1" || debug.get("enableAapiCta");
        setOverrideUserAgent(enableAapiCta);

        this.state = {
            theme: getThemeName() as Theme,
            locale: getCurrentLocale(),
            device: deviceDetails,
            domain: domain,
            isVisible: document.visibilityState === 'visible',
            isFullUrlSupported: fullUrlsSupported,
            isTeenAccount: isTeenAccount || debug.get("forceTeenAccount"),
            enableSeeAllDetails: !shouldDisableSeeAllDetails,
            enableAapiCta: enableAapiCta,
            enableMes: enableMes,
            enableRekindle: (reKindleCountryEnabled && reKindleWeblabsEnabled) || debug.get("enableRekindle"),
            enableRekindle2: (reKindle2Eligibility && reKindle2WeblabsEnabled) || debug.get("enableRekindle2"),
            enableAfrAcquisitions: iOSAFRTreatment === "T1" || debug.get("enableAfrAcquisitions"),
            enableKuUpsell,
            enableHeartCx: isBeta || heartCxTreatment === "T1" || debug.get("enableHeartCx"),
            enableReturnBorrowWithSave: returnBorrowSaveTreatment === "T1" || debug.get("enableReturnBorrowWithSave"),
            enableKuRebrand: kuRebrandTreatment === "T1" || debug.get("enableKuRebrand"),
            bypassAskConsent: iOSBypassAskConsentTreatment === "T1" || debug.get("bypassAskConsent"),
            debugFlagsTimestamp: Date.now(),
            ssoConfig: ssoEnabled ? {
                assocHandle: getIOSAssocHandle(domain),
                destinationDomain: domain
            } : undefined,
        };
        if (reKindle2WeblabsEnabled) {
            checkRekindle2Eligibility()
                .then(() => {
                    this.setState({
                        enableRekindle2: (reKindle2Eligibility && reKindle2WeblabsEnabled) || debug.get("enableRekindle2")
                    });
                });
        }
        debug.registerListener(() => {
            debug.log("debug.listener");
            const suppressSeeAllDetails = (reKindleWeblabsEnabled && appleCountryCode && !reKindleCountryEnabled) || isTeenAccount || debug.get("forceTeenAccount");
            const enableAapiCta = (isBeta && enableAapiCtaTreatment === "T2") || enableAapiCtaTreatment === "T1" || debug.get("enableAapiCta");
            setOverrideUserAgent(enableAapiCta);
            const debugSsoEnabled = iOSReKindleSSoTreatment === "T1" || debug.get("enableReKindleSSO");
            this.setState({
                isTeenAccount: isTeenAccount || debug.get("forceTeenAccount"),
                enableSeeAllDetails: !suppressSeeAllDetails,
                enableAapiCta: enableAapiCta,
                enableMes: (isBeta || iOSMESTreatment === "T1" || debug.get("enableMoreLikeThis")),
                enableRekindle: (reKindleCountryEnabled && reKindleWeblabsEnabled) || debug.get("enableRekindle"),
                enableRekindle2: (reKindle2Eligibility && reKindle2WeblabsEnabled) || debug.get("enableRekindle2"),
                enableAfrAcquisitions: iOSAFRTreatment === "T1" || debug.get("enableAfrAcquisitions"),
                enableKuUpsell: debug.get("enableKuUpsell"),
                enableHeartCx: isBeta || debug.get("enableHeartCx"),
                enableReturnBorrowWithSave: returnBorrowSaveTreatment === "T1" || debug.get("enableReturnBorrowWithSave"),
                enableKuRebrand: kuRebrandTreatment === "T1" || debug.get("enableKuRebrand"),
                bypassAskConsent: iOSBypassAskConsentTreatment === "T1" || debug.get("bypassAskConsent"),
                debugFlagsTimestamp: Date.now(),
                ssoConfig: debugSsoEnabled ? {
                    assocHandle: getIOSAssocHandle(domain),
                    destinationDomain: domain
                } : undefined,
            });
        });
        onLocaleChange(this.onLocaleChangeCallback);
        onThemeChange(this.onThemeChangeCallback);
        onVisibilityChange(this.onVisibilityChangeCallback);
        // To enable ReKindle app upgrade error CX on older versions of the iOS app, check server weblabs
        // if the client side weblab is not present.
        if (deviceDetails.isIOS && window.quickViewData?.experimentalFeatures?.treatmentMap?.["BOOKS_REKINDLE_LAUNCH_1190038"] === undefined) {
            weblabUtils.getTreatmentsWithTTL(["BOOKS_REKINDLE_LAUNCH_1190038", "BOOKS_REKINDLE_QUICKVIEW_FORCEOFF_1193837"])
                .then(treatments => {
                    const iOSReKindleTreatment = treatments["BOOKS_REKINDLE_LAUNCH_1190038"];
                    const iOSReKindleForceOffTreatment = treatments["BOOKS_REKINDLE_QUICKVIEW_FORCEOFF_1193837"];
                    reKindleWeblabsEnabled = iOSReKindleForceOffTreatment !== "T1" && iOSReKindleTreatment === "T1";
                    this.setState({
                        enableRekindle: reKindleWeblabsEnabled || debug.get("enableRekindle")
                    });
                });
        }
    }

    onLocaleChangeCallback = (locale: string) => {
        if (locale && locale !== this.state.locale) {
            this.setState({ locale });
        }
    };

    onThemeChangeCallback = (theme: Theme) => {
        if (theme !== this.state.theme) {
            this.setState({ theme });
        }
    };

    onVisibilityChangeCallback = (isVisible: boolean) => {
        if (isVisible !== this.state.isVisible) {
            this.setState({ isVisible });
        }
    };

    render() {
        return (
            <DeviceContext.Provider value={this.state}>
                <bds-render-context-provider
                    devicetype={getBDSDeviceType(this.state.device)}
                    hostapplication="kindle"
                    locale={getBDSLocale(this.state.locale)}
                    marketplace={getBDSMarketplace(this.state.domain)}
                    operatingsystem={getBDSOperatingSystem(this.state.device)}
                    pagetype="detailPage"
                    theme={this.state.theme}
                >
                    <Translations locale={this.state.locale}>
                        <HeartCxManager>
                            <QuickView />
                        </HeartCxManager>
                    </Translations>
                </bds-render-context-provider>
            </DeviceContext.Provider>
        );
    }
}

export default App;
